// =====================================================================
// Zona horaria — fuente ÚNICA de cálculo y formateo de fechas.
// =====================================================================
//
// Todo horario que se le muestra a un humano (UI, PDF, notificación) o se le
// pasa a la IA, y todo rango de fechas (filtros, stats), pasa por acá. El motivo:
// el server corre en UTC (Vercel) y la máquina del operador puede estar en
// cualquier zona — si formateáramos con la TZ "ambiente" (toLocaleString sin
// timeZone, o date-fns format que usa la del proceso) los horarios saldrían
// corridos. Acá la TZ es SIEMPRE explícita.
//
// TZ POR TENANT
// -------------
// La zona ya no está clavada a Argentina: cada tenant define la suya en
// settings.timezone (IANA, ej "America/Sao_Paulo"). Si no la setea, cae a
// DEFAULT_TZ (Argentina). De dónde sale la TZ "activa" según el contexto:
//   - Cliente: un browser = un solo tenant. `setActiveTz` lo fija una vez
//     (lo llama AuthProvider con la TZ del tenant), y los helpers la leen por
//     default. Por eso los callsites de cliente no pasan `tz` y "simplemente
//     andan" per-tenant.
//   - Server: NO hay TZ ambiente (un proceso atiende muchos tenants a la vez).
//     getActiveTz() devuelve SIEMPRE DEFAULT_TZ. El código server que necesita
//     la TZ del tenant la pasa EXPLÍCITA por parámetro (sale de la sesión / del
//     tenant cargado). Así un error de TZ en server es un parámetro que falta
//     (visible), no un fallback silencioso.
//
// Seguro en server y cliente (sin "server-only").

/** Zona por defecto si el tenant no configuró una. Argentina no usa DST. */
export const DEFAULT_TZ = "America/Argentina/Buenos_Aires";

/** Alias histórico. Equivale a DEFAULT_TZ; se mantiene por los imports vigentes. */
export const APP_TZ = DEFAULT_TZ;

// TZ activa del lado cliente. En server queda SIEMPRE en DEFAULT_TZ (ningún
// código server debe llamar setActiveTz: el estado de módulo se comparte entre
// requests y mezclaría tenants). El display de cliente la lee por default.
let activeTz = DEFAULT_TZ;

/** Fija la TZ activa del cliente. Llamar SOLO desde código de browser
 *  (AuthProvider) con la TZ del tenant logueado. No-op seguro si se pasa vacío. */
export function setActiveTz(tz: string | null | undefined): void {
  activeTz = tz || DEFAULT_TZ;
}

/** TZ activa: la del tenant en cliente; DEFAULT_TZ en server. */
export function getActiveTz(): string {
  return activeTz;
}

/** Resuelve la TZ de un tenant desde sus settings (default Argentina). */
export function tenantTz(
  settings: { timezone?: string | null } | null | undefined,
): string {
  return settings?.timezone || DEFAULT_TZ;
}

function toDate(value: string | Date | null | undefined): Date | null {
  if (value == null) return null;
  const d = typeof value === "string" ? new Date(value) : value;
  return Number.isNaN(d.getTime()) ? null : d;
}

// ¿El instante cae en el futuro respecto de "ahora"? Con un margen de gracia
// (default 5 min) para absorber el desfasaje de reloj entre el equipo del
// operador y el server. Un valor nullish/inválido NO cuenta como futuro
// (devuelve false): esa validación la hace otro chequeo aparte. Se usa para
// impedir cargar "hechos" con fecha futura (un incidente no puede haber pasado
// todavía). Seguro en server y cliente. (Comparación de instantes absolutos →
// no depende de TZ.)
export function isFutureInstant(
  value: string | Date | null | undefined,
  graceMs = 5 * 60 * 1000,
): boolean {
  const d = toDate(value);
  if (!d) return false;
  return d.getTime() > Date.now() + graceMs;
}

const DAY_MS = 24 * 60 * 60 * 1000;

// Reportes históricos: un hecho YA pasó, así que ningún horario puede caer en el
// futuro. Cuando el relato da horas "del día" sin fecha (ej "18:30") y esa hora
// es posterior a "ahora", en realidad el incidente fue AYER (o antes).
// `pastShiftDays` devuelve cuántos días ENTEROS hay que restarle a TODO el bloque
// de horarios para que el más tardío deje de estar en el futuro. Se corre todo
// junto (no se parte el incidente entre dos días): preserva la hora del día y los
// gaps relativos entre eventos. Devuelve 0 si ya está todo en el pasado (o no hay
// instantes válidos). Mismo `graceMs` que isFutureInstant (desfasaje de reloj).
export function pastShiftDays(
  values: Array<string | Date | null | undefined>,
  nowMs = Date.now(),
  graceMs = 5 * 60 * 1000,
): number {
  let maxTs = -Infinity;
  for (const v of values) {
    const d = toDate(v);
    if (d) maxTs = Math.max(maxTs, d.getTime());
  }
  if (maxTs === -Infinity) return 0;
  const over = maxTs - (nowMs + graceMs);
  if (over <= 0) return 0;
  return Math.ceil(over / DAY_MS);
}

// Resta `days` días enteros a un instante ISO (preserva la hora del día; el
// piloto no usa TZ con DST). Nullish / inválido / days<=0 → devuelve el valor tal
// cual (nullish colapsa a null). Pensado para aplicar el corrimiento que calcula
// `pastShiftDays`.
export function shiftIsoBackDays(
  value: string | null | undefined,
  days: number,
): string | null {
  const d = days > 0 ? toDate(value) : null;
  if (d) return new Date(d.getTime() - days * DAY_MS).toISOString();
  return value ?? null;
}

// =====================================================================
// Formateo para humanos (TZ explícita, default = TZ activa)
// =====================================================================

// "dd/mm hh:mm" (24h). Devuelve null si la entrada es nullish/inválida, para que
// el caller decida el placeholder (`fmtDateTime(x) ?? "(en curso)"`).
export function fmtDateTime(
  value: string | Date | null | undefined,
  tz: string = getActiveTz(),
): string | null {
  const d = toDate(value);
  if (!d) return null;
  return d.toLocaleString("es-AR", {
    timeZone: tz,
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

// "hh:mm" (24h). Mismas semánticas de null que fmtDateTime.
export function fmtTime(
  value: string | Date | null | undefined,
  tz: string = getActiveTz(),
): string | null {
  const d = toDate(value);
  if (!d) return null;
  return d.toLocaleTimeString("es-AR", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

// "hh:mm:ss" (24h). Para relojes / sellos con segundos.
export function fmtClock(
  value: string | Date | null | undefined,
  tz: string = getActiveTz(),
): string | null {
  const d = toDate(value);
  if (!d) return null;
  return d.toLocaleTimeString("es-AR", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

// Minutos desde medianoche en la TZ (0..1439). Para filtrar por "hora del día"
// sin importar la fecha (ej: "casos entre las 08:00 y las 16:00"). Devuelve null
// ante valor inválido.
export function localMinutesOfDay(
  value: string | Date | null | undefined,
  tz: string = getActiveTz(),
): number | null {
  const hhmm = fmtTime(value, tz);
  if (!hhmm) return null;
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

// Clave de día en la TZ ("aaaa-mm-dd"). Sirve para detectar cuándo dos
// timestamps caen en días distintos (separadores de fecha en el chat). en-CA
// formatea como ISO, así que la clave es ordenable y comparable por igualdad.
export function dayKey(
  value: string | Date | null | undefined,
  tz: string = getActiveTz(),
): string | null {
  const d = toDate(value);
  if (!d) return null;
  return d.toLocaleDateString("en-CA", { timeZone: tz });
}

// Etiqueta de día para humanos: "Hoy", "Ayer", o "Martes 15 de junio" (con año
// si es de otro año). Para los separadores de fecha del chat interno.
export function fmtDayLabel(
  value: string | Date | null | undefined,
  tz: string = getActiveTz(),
): string | null {
  const d = toDate(value);
  if (!d) return null;
  const key = dayKey(d, tz);
  const now = new Date();
  if (key === dayKey(now, tz)) return "Hoy";
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  if (key === dayKey(yesterday, tz)) return "Ayer";
  const sameYear = key?.slice(0, 4) === dayKey(now, tz)?.slice(0, 4);
  const label = d.toLocaleDateString("es-AR", {
    timeZone: tz,
    weekday: "long",
    day: "numeric",
    month: "long",
    ...(sameYear ? {} : { year: "numeric" }),
  });
  // es-AR devuelve "martes, 15 de junio" → sacamos la coma y capitalizamos.
  const clean = label.replace(",", "");
  return clean.charAt(0).toUpperCase() + clean.slice(1);
}

// "EEE dd MMM" → "mié 25 jun" (capitalizado, sin punto). Para listas compactas.
export function fmtDateShort(
  value: string | Date | null | undefined,
  tz: string = getActiveTz(),
): string | null {
  const d = toDate(value);
  if (!d) return null;
  const label = d.toLocaleDateString("es-AR", {
    timeZone: tz,
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
  // es-AR puede devolver "mié, 25 jun" o "mié 25 jun." → normalizamos.
  const clean = label.replace(/,/g, "").replace(/\./g, "").trim();
  return clean.charAt(0).toUpperCase() + clean.slice(1);
}

// "dd/mm/aaaa" (solo fecha numérica).
export function fmtDate(
  value: string | Date | null | undefined,
  tz: string = getActiveTz(),
): string | null {
  const d = toDate(value);
  if (!d) return null;
  return d.toLocaleDateString("es-AR", {
    timeZone: tz,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

// Día de la semana, capitalizado: "Martes". (date-fns "EEEE")
export function fmtWeekday(
  value: string | Date | null | undefined,
  tz: string = getActiveTz(),
): string | null {
  const d = toDate(value);
  if (!d) return null;
  const s = d.toLocaleDateString("es-AR", { timeZone: tz, weekday: "long" });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// "dd/mm/aaaa hh:mm:ss" (24h) — para pies de página, exports, "generado el ...".
export function fmtDateTimeLong(
  value: string | Date | null | undefined,
  tz: string = getActiveTz(),
): string | null {
  const d = toDate(value);
  if (!d) return null;
  return d.toLocaleString("es-AR", {
    timeZone: tz,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

// =====================================================================
// Rangos de día y "stamps" (DST-correctos vía Intl, sin dependencias)
// =====================================================================

const YMD = /^\d{4}-\d{2}-\d{2}$/;

function ymdOr(raw: string | null | undefined, fallback: string): string {
  return raw && YMD.test(raw) ? raw : fallback;
}

// Offset (ms) de `tz` respecto de UTC en un instante dado. offset = wall − utc.
// Funciona con DST: pregunta la hora de pared en `tz` para ese instante.
function tzOffsetMs(tz: string, instant: Date): number {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const p: Record<string, string> = {};
  for (const part of dtf.formatToParts(instant)) p[part.type] = part.value;
  const wallAsUtc = Date.UTC(
    Number(p.year),
    Number(p.month) - 1,
    Number(p.day),
    Number(p.hour),
    Number(p.minute),
    Number(p.second),
  );
  return wallAsUtc - instant.getTime();
}

// Instante UTC cuya hora de PARED en `tz` es ymd + (h,m,s,ms). Dos pasadas para
// resolver bien el offset alrededor de cambios de DST. Los ms se aplican aparte:
// Intl.formatToParts no expone milisegundos, así que el offset se computa sobre
// el segundo entero y los ms se suman al final (si no, se colaban en el offset).
function zonedWallToUtc(
  ymd: string,
  h: number,
  m: number,
  s: number,
  ms: number,
  tz: string,
): Date {
  const [y, mo, d] = ymd.split("-").map(Number);
  const guess = Date.UTC(y, mo - 1, d, h, m, s, 0);
  const off1 = tzOffsetMs(tz, new Date(guess));
  let utc = guess - off1;
  const off2 = tzOffsetMs(tz, new Date(utc));
  if (off2 !== off1) utc = guess - off2;
  return new Date(utc + ms);
}

/** "YYYY-MM-DD" del instante dado, leído en `tz`. en-CA formatea como ISO. */
export function dayStamp(date: Date = new Date(), tz: string = getActiveTz()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

/** "YYYY-MM-DD" del día actual en `tz`. */
export function todayStamp(tz: string = getActiveTz()): string {
  return dayStamp(new Date(), tz);
}

/** Instante de las 00:00:00.000 de `tz` del día (raw "YYYY-MM-DD"; fallback si inválido). */
export function dayStart(
  raw: string | null | undefined,
  fallback: string,
  tz: string = getActiveTz(),
): Date {
  return zonedWallToUtc(ymdOr(raw, fallback), 0, 0, 0, 0, tz);
}

/** Instante de las 23:59:59.999 de `tz` del día (raw "YYYY-MM-DD"; fallback si inválido). */
export function dayEnd(
  raw: string | null | undefined,
  fallback: string,
  tz: string = getActiveTz(),
): Date {
  return zonedWallToUtc(ymdOr(raw, fallback), 23, 59, 59, 999, tz);
}
